import React from 'react'
import styled, { css } from 'styled-components'

import {
  docsApiLinks,
  docsClipsLinks,
  docsOptionalLinks,
  docsResourceLinks,
  docsSdkLinks,
  docsTrustAndSafetyLinks,
  tosLinks,
} from '@/config/links'
import { TitleLink } from '@/types/title-link'
import { useTheme } from '@/styles'
import { ContextLink } from '@/components/common/context-link'

import { mobileViewQuery } from './const'

const Dropdown = styled.div<DropdownNavMenuProps>`
  display: flex;
  justify-content: center;
  height: 575px;
  background: ${({ theme }) =>
    `linear-gradient(-135deg, ${theme.palette.giphyGreen} 0%, ${theme.palette.giphyPurple} 100%)`};

  svg {
    width: auto;
    mix-blend-mode: normal;
    opacity: 0.8;
  }

  ${({ isFooter }) =>
    isFooter &&
    css`
      height: 100%;
      width: 100%;
      background: transparent;
    `}

  @media ${mobileViewQuery} {
    height: 100%;
    padding: 0 ${({ theme }) => theme.containerMobilePadding}px;
    box-sizing: border-box;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: ${({ theme }) => theme.containerWidth}px;
  height: 100%;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  padding: 70px ${({ theme }) => theme.containerPadding}px;

  @media ${mobileViewQuery} {
    width: 100%;
    padding: 50px 0;
  }
`

const MenuColumns = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-size: 20px;
  line-height: 28px;

  @media ${mobileViewQuery} {
    flex-wrap: wrap;
    margin-bottom: 4px;
  }
`

const MenuColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 0 8px;

  @media ${mobileViewQuery} {
    height: auto;
    flex-basis: 45%;
  }
`

const MenuColumnHeader = styled.h5`
  margin-bottom: 0;
`

const MenuColumnLinks = styled.div<DropdownNavMenuProps>`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin: 30px 0;
  line-height: 30px;
  font-family: 'PT Mono', monospace;

  @media ${mobileViewQuery} {
    margin: 26px 0 30px 0;
    line-height: 30px;
  }

  ${({ isFooter }) =>
    isFooter &&
    `
        -webkit-text-fill-color: white;
        font-weight: normal;
        `}
`

type MenuColumnAProps = {
  isFooter?: boolean
}

const MenuColumnA = styled(ContextLink)<MenuColumnAProps>`
  color: ${({ isFooter, theme }) => (isFooter ? `${theme.palette.giphyLightestGrey}` : 'white')};
  text-decoration: none;
  border-bottom: none;
  font-size: 18px;
  padding-bottom: 4px;

  &:hover {
    border-bottom: 1px solid
      ${({ isFooter, theme }) => (isFooter ? `${theme.palette.giphyLightestGrey}` : 'white')};
  }
`

const ToS = styled.div`
  display: grid;
  max-width: 800px;
  width: 100%;
  grid-template-columns: repeat(5, auto);
  align-self: flex-start;

  @media ${mobileViewQuery} {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
  }
`

const ToSLink = styled.a`
  color: white;
  font-size: 16px;
  text-decoration: none;
  border-bottom: none;
  margin-right: 20px;
  opacity: 0.6;

  &:last-child {
    margin-right: 0;
  }

  @media ${mobileViewQuery} {
    flex-basis: 50%;
    margin: 5px 0;
  }
`

type Cell = {
  title: string
  links: TitleLink[]
}

type Column = Cell | Cell[]

type DropdownNavMenuProps = {
  isFooter?: boolean
}

const columns: Column[] = [
  { title: 'SDKs', links: docsSdkLinks },
  [
    { title: 'API', links: docsApiLinks },
    { title: 'CLIPS API', links: docsClipsLinks },
  ],
  { title: 'Optional Settings', links: docsOptionalLinks },
  { title: 'Trust And Safety', links: docsTrustAndSafetyLinks },
  { title: 'Resources', links: docsResourceLinks },
]

export const DropdownNavMenu = ({ isFooter }: DropdownNavMenuProps) => {
  const { palette } = useTheme()
  const headerColors = [
    palette.giphyIndigo,
    palette.giphyLightBlue,
    palette.giphyBlue,
    palette.giphyAqua,
  ]

  return (
    <Dropdown isFooter={isFooter}>
      <Content>
        <MenuColumns>
          {columns.map((cells, index) => (
            <MenuColumn key={index}>
              {(Array.isArray(cells) ? cells : [cells]).map((cell, subIndex) => (
                <React.Fragment key={subIndex}>
                  <MenuColumnHeader
                    style={{ color: isFooter ? headerColors[index] : palette.giphyWhite }}
                  >
                    {cell.title}
                  </MenuColumnHeader>
                  <MenuColumnLinks isFooter={isFooter}>
                    {cell.links.map((item, index) => (
                      <span key={index}>
                        <MenuColumnA to={item.link} isFooter={isFooter}>
                          {item.title}
                        </MenuColumnA>
                      </span>
                    ))}
                  </MenuColumnLinks>
                </React.Fragment>
              ))}
            </MenuColumn>
          ))}
        </MenuColumns>
        <ToS>
          {tosLinks.map((item, index) => (
            <ToSLink href={item.link} key={index}>
              {item.title}
            </ToSLink>
          ))}
        </ToS>
      </Content>
    </Dropdown>
  )
}
